<template>
    <section class="px-4 pb-10 mx-auto max-w-5xl bg-white">
        <div class="mx-auto max-w-6xl">
            <!-- Header -->
            <div class="mb-12 text-center">
                <h2 class="mb-4 text-2xl leading-5 sm:text-3xl md:text-32 font-ww-bold" v-html="mainHeading">
                </h2>
            </div>

            <!-- Grid of ingredients -->
            <div class="grid grid-cols-1 gap-y-6 md:gap-y-12 sm:gap-x-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-24 2xl:gap-x-32 lg:gap-y-10">
                <!-- Loop through showcase items -->
                <div v-for="(item, index) in showcaseItems" :key="index" class="flex flex-col text-center">
                    <div class="flex items-center mb-4 h-40">
                        <nuxt-img 
                            class="mx-auto" 
                            :width="item.image.width" 
                            :height="item.image.height" 
                            :src="item.image.src" 
                            :alt="item.title"
                            format="webp"
                            loading="lazy"
                            sizes="xs:170px sm:170px md:170px lg:170px xl:251px"
                        />
                    </div>
                <div>
                    <h3 class="mb-2 !leading-5 md:text-left text-center text-base lg:text-lg font-ww-bold" v-html="item.title">
                    </h3>
                    <div class="mt-4 text-base !leading-6 text-left lg:text-lg" v-html="item.description">
                    </div>
                </div>
                </div>
            </div>
        </div>

        <div class="mt-8 w-full md:mt-16">
            <button @click="scrollTo(buttonHref,'bottom')" class="flex gap-x-4 justify-center items-center px-4 py-3.5 mx-auto w-10/12 text-lg text-white rounded-full btn-red sm:w-7/12 md:text-xl font-ww-medium font-bold" :style="{ backgroundColor: buttonColor }">
                {{ buttonText }}

                <span>
                    <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12.8593 0.338767C12.7655 0.43173 12.6911 0.542331 12.6404 0.66419C12.5896 0.78605 12.5635 0.916755 12.5635 1.04877C12.5635 1.18078 12.5896 1.31148 12.6404 1.43334C12.6911 1.5552 12.7655 1.6658 12.8593 1.75877L17.4593 6.35877L1.05859 6.17123C0.793377 6.17123 0.539023 6.27659 0.351487 6.46412C0.163951 6.65166 0.0585938 6.90601 0.0585938 7.17123C0.0585938 7.43645 0.163951 7.6908 0.351487 7.87834C0.539023 8.06587 0.793377 8.17123 1.05859 8.17123L17.4393 8.35877L12.8593 12.9288C12.673 13.1161 12.5685 13.3696 12.5685 13.6338C12.5685 13.898 12.673 14.1514 12.8593 14.3388C13.0466 14.525 13.3001 14.6296 13.5643 14.6296C13.8284 14.6296 14.0819 14.525 14.2693 14.3388L20.6293 7.97877C20.7149 7.89664 20.783 7.79805 20.8295 7.68892C20.876 7.5798 20.9 7.4624 20.9 7.34377C20.9 7.22514 20.876 7.10773 20.8295 6.99861C20.783 6.88949 20.7149 6.7909 20.6293 6.70877L14.2793 0.338767C14.1863 0.245039 14.0757 0.170645 13.9538 0.119876C13.832 0.0691072 13.7013 0.0429688 13.5693 0.0429688C13.4372 0.0429688 13.3065 0.0691072 13.1847 0.119876C13.0628 0.170645 12.9522 0.245039 12.8593 0.338767Z" fill="white"/>
</svg>
                </span>
            </button>
        </div>
    </section>
</template>

<script setup>
defineProps({
  mainHeading: {
    type: String,
    default: 'What\'s Inside? Pure Brain Power'
  },
  showcaseItems: {
    type: Array,
    default: () => [
      {
        title: 'GeniusPure® Alpha-GPC – Instant Focus, No Jitters',
        description: 'Boosts acetylcholine, the key neurotransmitter for memory, attention & learning. Users report 100% sharper focus in one dose.',
        image: {
          src: '/assets/images/brain-power-1.png',
          width: 170,
          height: 168
        }
      },
      {
        title: 'CognatiQ® Coffee Fruit Extract – Supercharges Brain Growth',
        description: 'Clinically shown to boost BDNF by 143%, the brain\'s natural "growth hormone" for memory and problem-solving.',
        image: {
          src: '/assets/images/brain-power-2.png',
          width: 235,
          height: 130
        }
      },
      {
        title: 'Organic Lion\'s Mane & Chaga Mushrooms – Nature\'s Smart Drugs',
        description: 'Supports neuroprotection, stress resilience, and nerve regeneration for long-term cognitive health.',
        image: {
          src: '/assets/images/brain-power-3.png',
          width: 235,
          height: 130
        }
      },
      {
        title: 'Grass-Fed Collagen – Brain & Body Support',
        description: 'Collagen isn\'t just for skin—it\'s critical for brain health. Helps maintain neuron structure, supports brain elasticity, and enhances cognitive resilience.',
        image: {
          src: '/assets/images/brain-power-4.png',
          width: 251,
          height: 167
        }
      },
      {
        title: 'ElectroPrime® Electrolytes – Hydration for Your Brain',
        description: 'Keeps neurons firing at peak performance and reduces brain fog by 67%.',
        image: {
          src: '/assets/images/brain-power-5.png',
          width: 248,
          height: 170
        }
      },
      {
        title: 'Organic, Low-Acid Coffee – Energy Without the Jitters',
        description: 'Smoother, gentler on your stomach, and packed with antioxidants to fuel your brain the clean way.',
        image: {
          src: '/assets/images/brain-power-6.png',
          width: 264,
          height: 117
        }
      }
    ]
  },
  buttonText: {
    type: String,
    default: 'ORDER NOW'
  },
  buttonColor: {
    type: String,
    default: '#DC2626'
  },
  buttonHref: {
    type: String,
    default: 'products'
  }
})

defineBuilderComponent({
    friendlyName: 'Content Showcase',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'What\'s Inside? Pure Brain Power'
        },
        {
            name: 'showcaseItems',
            type: 'list',
            subFields: [
                {
                    name: 'title',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'description',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'image',
                    type: 'object',
                    subFields: [
                        {
                            name: 'src',
                            type: 'file',
                            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
                            defaultValue: ''
                        },
                        {
                            name: 'width',
                            type: 'number',
                            defaultValue: 200
                        },
                        {
                            name: 'height',
                            type: 'number',
                            defaultValue: 150
                        }
                    ],
                    defaultValue: {
                        src: '',
                        width: 200,
                        height: 150
                    }
                }
            ],
            defaultValue: [
                {
                    title: 'GeniusPure® Alpha-GPC – Instant Focus, No Jitters',
                    description: 'Boosts acetylcholine, the key neurotransmitter for memory, attention & learning. Users report 100% sharper focus in one dose.',
                    image: {
                        src: '/assets/images/brain-power-1.png',
                        width: 170,
                        height: 168
                    }
                },
                {
                    title: 'CognatiQ® Coffee Fruit Extract – Supercharges Brain Growth',
                    description: 'Clinically shown to boost BDNF by 143%, the brain\'s natural "growth hormone" for memory and problem-solving.',
                    image: {
                        src: '/assets/images/brain-power-2.png',
                        width: 235,
                        height: 130
                    }
                },
                {
                    title: 'Organic Lion\'s Mane & Chaga Mushrooms – Nature\'s Smart Drugs',
                    description: 'Supports neuroprotection, stress resilience, and nerve regeneration for long-term cognitive health.',
                    image: {
                        src: '/assets/images/brain-power-3.png',
                        width: 235,
                        height: 130
                    }
                },
                {
                    title: 'Grass-Fed Collagen – Brain & Body Support',
                    description: 'Collagen isn\'t just for skin—it\'s critical for brain health. Helps maintain neuron structure, supports brain elasticity, and enhances cognitive resilience.',
                    image: {
                        src: '/assets/images/brain-power-4.png',
                        width: 251,
                        height: 167
                    }
                },
                {
                    title: 'ElectroPrime® Electrolytes – Hydration for Your Brain',
                    description: 'Keeps neurons firing at peak performance and reduces brain fog by 67%.',
                    image: {
                        src: '/assets/images/brain-power-5.png',
                        width: 248,
                        height: 170
                    }
                },
                {
                    title: 'Organic, Low-Acid Coffee – Energy Without the Jitters',
                    description: 'Smoother, gentler on your stomach, and packed with antioxidants to fuel your brain the clean way.',
                    image: {
                        src: '/assets/images/brain-power-6.png',
                        width: 264,
                        height: 117
                    }
                }
            ]
        },
        {
            name: 'buttonText',
            type: 'text',
            defaultValue: 'ORDER NOW'
        },
        {
            name: 'buttonColor',
            type: 'color',
            defaultValue: '#DC2626'
        },
        {
            name: 'buttonHref',
            type: 'text',
            defaultValue: 'products'
        }
    ]
})
</script>